import React, { useState, useEffect } from "react";
import { 
  Card, 
  Tabs, 
  Button, 
  message, 
  Space, 
  Typography,
  Form,
  Row,
  Col,
  Divider,
  Input  // Input hinzugefügt
} from "antd";
import { SaveOutlined, EyeOutlined, DownloadOutlined, BookOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { GuestmapData } from "./types/Guestmap";
import { BookingTab } from "./Tabs/BookingTab";
import { HostTab } from "./Tabs/HostTab";
import { WelcomeTab } from "./Tabs/WelcomeTab";
import { ServicesTab } from "./Tabs/ServicesTab";
import { AttractionsTab } from "./Tabs/AttractionsTab";
import { EmergencyTab } from "./Tabs/EmergencyTab";
import { API_CONFIG } from "../../config/api";

const { Title, Text } = Typography;

export const GuestmapEditor: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("booking");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Initiale Daten für die Gästemappe (muss vor loadGuestmapData definiert sein)
  const initialData: GuestmapData & { templateName?: string; templateDescription?: string } = {
    templateName: "",  // Neues Feld für Name
    templateDescription: "",  // Neues Feld für Beschreibung
    booking: {
      id: "BK2024-001",
      checkIn: "2024-01-15",
      checkOut: "2024-01-20",
      roomNumber: "101",
      roomType: "Suite",
      doorCode: "4711",
      numberOfGuests: 2,
      petsAllowed: false,
      numberOfPets: 0
    },
    host: {
      name: "Max Mustermann",
      businessName: "Musterhof",
      address: {
        street: "Musterstraße 1",
        zipCode: "12345",
        city: "Musterstadt",
        country: "Deutschland"
      },
      phone: "+49 123 456789",
      email: "info@musterhof.de"
    },
    content: {
      welcomeLetter: {
        salutation: "Liebe Gäste,",
        text: [
          "herzlich willkommen in unserem Haus! Wir freuen uns sehr, Sie bei uns begrüßen zu dürfen und hoffen, dass Sie sich während Ihres Aufenthalts rundum wohlfühlen werden.",
          "Diese digitale Gästemappe wurde speziell für Sie zusammengestellt und enthält alle wichtigen Informationen für einen angenehmen Aufenthalt. Hier finden Sie alles, was Sie wissen müssen - von praktischen Hinweisen bis hin zu Empfehlungen für Ihre Freizeit.",
          "Sollten Sie Fragen haben oder Hilfe benötigen, zögern Sie bitte nicht, uns zu kontaktieren. Wir sind jederzeit für Sie da!"
        ],
        signature: "Ihr Gastgeber-Team"
      },
      weather: {
        current: {
          temperature: 22,
          condition: "Sonnig",
          icon: "sun",
          feelsLike: 25,
          humidity: 65,
          windSpeed: 8
        },
        forecast: [
          { day: "Mo", icon: "sun", high: 24, low: 15 },
          { day: "Di", icon: "cloud-rain", high: 19, low: 12 },
          { day: "Mi", icon: "cloud-sun", high: 21, low: 14 },
          { day: "Do", icon: "sun", high: 25, low: 16 },
          { day: "Fr", icon: "sun", high: 26, low: 17 },
          { day: "Sa", icon: "cloud", high: 23, low: 15 },
          { day: "So", icon: "sun", high: 24, low: 16 }
        ]
      },
      services: [
        {
          name: "Gasthaus zur Sonne",
          type: "restaurant",
          distance: "2 min zu Fuß",
          description: "Traditionelle deutsche Küche",
          rating: 4.5,
          image: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=200&h=150&fit=crop"
        },
        {
          name: "Dr. med. Schmidt",
          type: "medical",
          distance: "10 min zu Fuß",
          description: "Hausarzt",
          phone: "0123 456789",
          address: "Hauptstraße 45"
        }
      ],
      attractions: [
        {
          name: "Historisches Schloss",
          distance: "15 min zu Fuß",
          description: "Wunderschönes Barockschloss mit Garten",
          category: "Kultur",
          image: "https://images.unsplash.com/photo-1467164616789-ce7ae65ab4c9?w=300&h=200&fit=crop"
        }
      ],
      emergency: {
        general: [
          { number: "112", description: "Feuerwehr & Rettungsdienst" },
          { number: "110", description: "Polizei" }
        ],
        house: [
          { number: "+49 123 456789", description: "Gastgeber" }
        ],
        additional: [],
        address: "Musterstraße 1, 12345 Musterstadt"
      }
    }
  };
  
  useEffect(() => {
    if (isEditMode) {
      loadGuestmapData(id);
    }
  }, [id]);

  const loadGuestmapData = async (templateId: string) => {
    try {
      setLoading(true);
      
      const response = await fetch(`/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getGuestmapTemplate&tx_buchungsmanager_frontend[id]=${templateId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.template) {
          // Transformiere die Daten für das Formular
          const formData = {
            templateName: data.template.name || '',
            templateDescription: data.template.description || '',
            booking: data.template.booking || initialData.booking,
            host: data.template.host || initialData.host,
            content: data.template.content || initialData.content
          };
          
          form.setFieldsValue(formData);
          message.success('Vorlage erfolgreich geladen');
        } else {
          message.error('Fehler beim Laden der Vorlage');
        }
      } else {
        message.error('Vorlage konnte nicht geladen werden');
      }
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      message.error('Fehler beim Laden der Vorlage');
    } finally {
      setLoading(false);
    }
  };

  // Form mit initialen Daten setzen
  React.useEffect(() => {
    if (!isEditMode) {
      form.setFieldsValue(initialData);
    }
  }, [form, isEditMode]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      const values = await form.validateFields();
      console.log('📤 Daten zum Speichern:', values);
      
      // Extrahiere Name und Beschreibung
      const { templateName, templateDescription, ...guestmapData } = values;
      
      // Erweitere die Daten um Name und Beschreibung
      const dataToSave = {
        name: templateName,
        description: templateDescription,
        ...guestmapData
      };
      
      // Unterschiedliche Actions für Create und Update
      const action = isEditMode ? 'updateGuestmapTemplate' : 'saveGuestmap';
      const apiUrl = `/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=${action}${isEditMode ? `&tx_buchungsmanager_frontend[id]=${id}` : ''}`;
      console.log('🌐 API-URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSave)
      });

      console.log('📡 API-Antwort Status:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('📥 API-Antwort:', result);
        
        if (result.success) {
          message.success(isEditMode ? 'Gästemappe erfolgreich aktualisiert!' : 'Gästemappe erfolgreich gespeichert!');
          console.log('✅ Gästemappe gespeichert mit ID:', result.data?.id);
          
          // Nach dem Speichern zur Vorlagen-Liste navigieren
          setTimeout(() => {
            navigate('/gaestemappen-vorlagen');
          }, 1500);
        } else {
          throw new Error(result.error || 'Fehler beim Speichern');
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP-Fehler:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

    } catch (error) {
      console.error('❌ Fehler beim Speichern:', error);
      message.error('Fehler beim Speichern der Gästemappe: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    // Vorschau-Funktionalität (später implementieren)
    message.info('Vorschau-Funktion wird implementiert');
  };

  const handleExport = () => {
    // Export-Funktionalität (später implementieren)
    message.info('Export-Funktion wird implementiert');
  };

  const tabItems = [
    {
      key: "booking",
      label: "Buchung",
      children: <BookingTab form={form} />
    },
    {
      key: "host",
      label: "Gastgeber",
      children: <HostTab form={form} />
    },
    {
      key: "welcome",
      label: "Willkommensinfo",
      children: <WelcomeTab form={form} />
    },
    {
      key: "services",
      label: "Services",
      children: <ServicesTab form={form} />
    },
    {
      key: "attractions",
      label: "Sehenswürdigkeiten",
      children: <AttractionsTab form={form} />
    },
    {
      key: "emergency",
      label: "Notfallnummern",
      children: <EmergencyTab form={form} />
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <BookOutlined style={{ marginRight: '8px' }} />
            Digitale Gästemappe {isEditMode ? 'bearbeiten' : 'erstellen'}
          </Title>
          <Text type="secondary">
            {isEditMode ? 'Bearbeiten Sie eine bestehende' : 'Erstellen Sie eine neue'} digitale Gästemappe für Ihre Gäste
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={initialData}
        >
          {/* Neue Felder für Name und Beschreibung */}
          <Card style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  name="templateName"
                  label="Name der Gästemappe"
                  rules={[
                    { required: true, message: 'Bitte geben Sie einen Namen für die Gästemappe ein' },
                    { max: 255, message: 'Der Name darf maximal 255 Zeichen lang sein' }
                  ]}
                >
                  <Input 
                    placeholder="z.B. Standard Gästemappe Ferienhaus" 
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="templateDescription"
                  label="Beschreibung (optional)"
                  rules={[
                    { max: 500, message: 'Die Beschreibung darf maximal 500 Zeichen lang sein' }
                  ]}
                >
                  <Input.TextArea 
                    placeholder="Kurze Beschreibung der Vorlage..."
                    rows={2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{ marginBottom: '24px' }}
          />

          <Divider />

          <Row justify="end">
            <Space>
              <Button 
                icon={<EyeOutlined />} 
                onClick={handlePreview}
              >
                Vorschau
              </Button>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleExport}
              >
                Exportieren
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                loading={loading}
                onClick={handleSubmit}
              >
                Speichern
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>
    </div>
  );
}; 